import React, { useState, useEffect, useImperativeHandle } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import withNavigate from '../../components/common/withNavigate';
import './Registration.css';
import API from '../../utils/API';
import CheckEmailModal from './CheckEmailModal';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import ModalPasswordInput from '../../components/Modals/ModalInputs/ModalPasswordInput';
import commonFunctions from '../../utils/commonFunctions';
import useValidateFields from '../../components/common/useValidateFields';

function Registration(props) {
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });
  const {
    onSuccess,
    closeButton,
    setSuccess: setSuccessProp,
    setError: setErrorProp,
    registrationRef,
    isModal = false,
  } = props;

  const currentPath = window.location.pathname;

  // Consolidated form data state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    companyPhone: '',
    companyAddress: '',
    password: '',
    passwordConfirm: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Use the useValidateFields hook for validation logic
  const {
    firstNameValid,
    lastNameValid,
    phoneValid,
    emailValid,
    companyNameValid,
    companyPhoneValid,
    companyAddressValid,
    passwordValid,
    passwordConfirmValid
  } = useValidateFields(
    {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      companyName: formData.companyName,
      companyPhone: formData.companyPhone,
      companyAddress: formData.companyAddress,
      password: formData.password,
      passwordConfirm: formData.passwordConfirm
    }
  );

  // Reset fields when component is mounted or remounted
  useEffect(() => {
    resetFields();
  }, []);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
  };

  const handleCompanyPhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      companyPhone: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Perform validations
    if (
      !firstNameValid &&
      !lastNameValid &&
      !emailValid &&
      !phoneValid &&
      !companyNameValid &&
      !companyPhoneValid &&
      !companyAddressValid &&
      !passwordValid &&
      !passwordConfirmValid
    ) {
      handleSignUp(e);
      setSuccess(<FormattedMessage id="TENANT_REGISTERED_SUCCESS" />);
    } else {
      setError(<FormattedMessage id="SUBMIT_ERROR" />);
      setSuccess(null);
    }
  };

  /**
   * Handle sign-up click and register user.
   */
  function handleSignUp(event) {
    event.preventDefault();

    API.register({
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phone: formData.phone,
      email: formData.email.trim(),
      company: formData.companyName.trim(),
      address: formData.companyAddress.trim(),
      companyPhone: formData.companyPhone,
      password: formData.password.trim(),
    })
      .then((res) => {
        const { data } = res;
        if (data.error) {
          setError(data.error);
          setSuccess(null);
          return;
        } else {
          setSuccess(<FormattedMessage id="TENANT_REGISTERED_SUCCESS" />);
          if (onSuccess) {
            onSuccess();
          }
          setError(null);
          if (currentPath === '/registration') {
            openCheckEmailModal();
          }
        }
      })
      .catch((err) => {
        setError(err.response ? err.response.data : <FormattedMessage id="SIGN_UP_ERROR" />);
        setSuccess(null);
        if (setErrorProp && typeof setErrorProp === 'function') {
          setErrorProp(err.response ? err.response.data : <FormattedMessage id="SIGN_UP_ERROR" />);
        }
        if (setSuccessProp && typeof setSuccessProp === 'function') {
          setSuccessProp(null);
        }
      });
  }

  /**
   * Reset fields of registration modal.
   */
  const resetFields = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyPhone: '',
      companyName: '',
      companyAddress: '',
      password: '',
      passwordConfirm: ''
    });
    setSubmitted(false);
  };

  // Expose the resetFields function to the parent component through the ref
  useImperativeHandle(registrationRef, () => ({
    resetFields,
  }));

  /**
   * Opens check email modal
   */
  function openCheckEmailModal() {
    document.getElementById("backdrop").style.display = "block";
    document.getElementById("checkEmailModal").style.display = "block";
    document.getElementById("checkEmailModal").classList.add("show");
  }

  /**
   * Closes modal.
   */
  function closeCheckEmailModal() {
    document.getElementById("backdrop").style.display = "none";
    document.getElementById("checkEmailModal").style.display = "none";
    document.getElementById("checkEmailModal").classList.remove("show");
  }

  /**
   * Handle click done in check email modal.
   */
  function handleDone(event) {
    event.preventDefault();
    if (currentPath === '/registration') {
      closeCheckEmailModal();
      props.navigate('/login');
    }
  }

  const className = isModal ? 'registration modal-registration' : 'registration';

  return (
    <div className={`${className} container py-5 h-100 w-50`}>
      <form>
        <div className="card">
          <div className="row mb-2">
            {isModal && (
              <button type="button" className="btn-close" id="registrationModalClose" data-bs-dismiss="modal" aria-label="Close"></button>
            )}
            <div className="header text-center">
              <h2><FormattedMessage id="CREATE_AN_ACCOUNT" /></h2>
            </div>
            <div className="col me-2">
              <div className="form-outline mt-2">
                <label className="form-label" htmlFor="firstNameInputAdd">
                  <FormattedMessage id="FIRST_NAME" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <input
                  id="firstNameInputAdd"
                  onChange={handleChange}
                  value={formData.firstName}
                  name="firstName"
                  className={`form-control rounded-1 ${submitted && (!firstNameValid ? 'is-valid' : 'is-invalid')}`}
                />
                {submitted && firstNameValid && commonFunctions.InvalidFeedbackString(firstNameValid, "FIRST_NAME")}
              </div>
            </div>
            <div className="col">
              <div className="form-outline mt-2">
                <label className="form-label" htmlFor="lastNameInputAdd">
                  <FormattedMessage id="LAST_NAME" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <input
                  id="lastNameInputAdd"
                  onChange={handleChange}
                  value={formData.lastName}
                  name="lastName"
                  className={`form-control rounded-1 ${submitted && (!lastNameValid ? 'is-valid' : 'is-invalid')}`}
                />
                {submitted && lastNameValid && commonFunctions.InvalidFeedbackString(lastNameValid, "LAST_NAME")}
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col me-2">
              <div className="form-outline">
                <label className="form-label" htmlFor="emailInputAdd">
                  <FormattedMessage id="EMAIL" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <input
                  id="emailInputAdd"
                  onChange={handleChange}
                  value={formData.email}
                  name="email"
                  className={`form-control rounded-1 ${submitted && (!emailValid ? 'is-valid' : 'is-invalid')}`}
                />
                {submitted && emailValid && commonFunctions.InvalidFeedbackString(emailValid, "EMAIL")}
              </div>
            </div>
            <div className="col">
              <label className="form-label" htmlFor="phoneInputAdd">
                <FormattedMessage id="PHONE" />
                <FormattedMessage id="SYMBOL_COLON" />
              </label>
              <PhoneInput
                id="phoneInputAdd"
                onChange={handlePhoneChange}
                name="phone"
                className={`form-control rounded-1 ${submitted && (!phoneValid ? 'is-valid' : 'is-invalid')}`}
                placeholder={translation("ENTER_PHONE_NUMBER")}
                value={formData.phone}
              />
              {submitted && phoneValid && commonFunctions.InvalidFeedbackString(phoneValid, "PHONE")}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col me-2">
              <div className="form-outline">
                <label className="form-label" htmlFor="companyNameInputAdd">
                  <FormattedMessage id="COMPANY_NAME" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <input
                  id="companyNameInputAdd"
                  onChange={handleChange}
                  value={formData.companyName}
                  name="companyName"
                  className={`form-control rounded-1 ${submitted && (!companyNameValid ? 'is-valid' : 'is-invalid')}`}
                />
                {submitted && companyNameValid && commonFunctions.InvalidFeedbackString(companyNameValid, "COMPANY_NAME")}
              </div>
            </div>
            <div className="col">
              <div className="form-outline">
                <label className="form-label" htmlFor="companyPhoneInputAdd">
                  <FormattedMessage id="COMPANY_PHONE" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <PhoneInput
                  id="companyPhoneInputAdd"
                  onChange={handleCompanyPhoneChange}
                  name="companyPhone"
                  className={`form-control rounded-1 ${submitted && (!companyPhoneValid ? 'is-valid' : 'is-invalid')}`}
                  placeholder={translation("ENTER_PHONE_NUMBER")}
                  value={formData.companyPhone}
                />
                {submitted && companyPhoneValid && commonFunctions.InvalidFeedbackString(companyPhoneValid, "COMPANY_PHONE")}
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="form-outline">
              <label className="form-label" htmlFor="companyAddressInputAdd">
                <FormattedMessage id="COMPANY_ADDRESS" />
                <FormattedMessage id="SYMBOL_COLON" />
              </label>
              <input
                id="companyAddressInputAdd"
                onChange={handleChange}
                value={formData.companyAddress}
                name="companyAddress"
                className={`form-control rounded-1 ${submitted && (!companyAddressValid ? 'is-valid' : 'is-invalid')}`}
              />
              {submitted && companyAddressValid && commonFunctions.InvalidFeedbackString(companyAddressValid, "COMPANY_ADDRESS")}
            </div>
          </div>
          <div className="row mb-2">
            <div className="form-outline">
              <label className="form-label" htmlFor="passwordInputAdd">
                <FormattedMessage id="PASSWORD" />
                <FormattedMessage id="SYMBOL_COLON" />
              </label>
              <ModalPasswordInput
                id="passwordInputAdd"
                onChange={handleChange}
                value={formData.password}
                name="password"
                isSubmitted={submitted}
                isValid={passwordValid}
                className={`form-control rounded-1 ${submitted && (!passwordValid ? 'is-valid' : 'is-invalid')}`}
              />
              {submitted && passwordValid && commonFunctions.InvalidFeedbackString(passwordValid, "PASSWORD", "d-block")}
            </div>
          </div>
          <div className="row mb-2">
            <div className="form-outline">
              <label className="form-label" htmlFor="passwordConfirmInputAdd">
                <FormattedMessage id="CONFIRM_PASSWORD" />
                <FormattedMessage id="SYMBOL_COLON" />
              </label>
              <ModalPasswordInput
                id="passwordConfirmInputAdd"
                onChange={handleChange}
                value={formData.passwordConfirm}
                name="passwordConfirm"
                isSubmitted={submitted}
                isValid={passwordConfirmValid}
                className={`form-control rounded-1 ${submitted && (!passwordConfirmValid ? 'is-valid' : 'is-invalid')}`}
              />
              {submitted && passwordConfirmValid && commonFunctions.InvalidFeedbackString(passwordConfirmValid, "CONFIRM_PASSWORD", "d-block")}
            </div>
          </div>
          <div className="text-center">
            <button id="confirmAddAccountBtn" type="button" className="mt-2 btn btn-primary btn-block signUpBtn" onClick={handleSubmit}>
              <FormattedMessage id="ADD_ACCOUNT" />
            </button>
          </div>
        </div>
      </form>
      <CheckEmailModal error={error} handleDone={handleDone} />
    </div>
  );
}

export default withNavigate(Registration);